import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Landmark, FileDown, Loader2 } from 'lucide-react';

const fmt$ = (n) => n == null ? '—' : n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

export default function QuoteTaxPanel({ api, submissionId, apiBase }) {
  const { user } = useAuth();
  const isStaff = ['mga_admin', 'platform_owner', 'underwriter'].includes(user?.role);
  const [data, setData] = useState(null);
  const [turnover, setTurnover] = useState('');
  const [signedBy, setSignedBy] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/documents/tax-breakdown/${submissionId}`).then(r => setData(r.data)).catch(() => {});
  }, [api, submissionId]);
  useEffect(() => { load(); }, [load]);

  if (!data || !data.gross_premium) return null;
  const t = data.tax;

  const record = async () => {
    const agg = parseFloat(turnover);
    if (!agg || !signedBy.trim()) { toast.error('Aggregated turnover and signatory are required'); return; }
    setSaving(true);
    try {
      await api.post('/documents/sbe-declaration/record', {
        entity_id: data.entity_id, aggregated_turnover: agg, signed_by: signedBy.trim(),
      });
      toast.success('Declaration recorded — NSW duty exemption now applies');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the declaration'); }
    setSaving(false);
  };

  const row = (label, amount, opts = {}) => (
    <div key={label} className={`flex items-baseline justify-between py-1 ${opts.total ? 'border-t font-bold' : ''}`}
      style={opts.total ? { borderColor: 'rgba(13,27,42,0.3)' } : {}}>
      <span className="text-xs" style={{ color: opts.total ? '#0D1B2A' : '#5C6B7A' }}>{label}
        {opts.badge && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(29,158,117,0.12)', color: '#1D9E75' }}>{opts.badge}</span>}
      </span>
      <span className="text-xs font-mono" style={{ color: '#0D1B2A' }}>{fmt$(amount)}</span>
    </div>
  );

  return (
    <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="quote-tax-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Landmark className="w-3.5 h-3.5" style={{ color: '#1A4C7C' }} />
          <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Premium Taxes — levy → GST → duty</h3>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }} data-testid="tax-split-source">
          {data.basis === 'garaging' ? 'Garaging basis' : 'Turnover basis'} · {data.split_source}
        </span>
      </div>

      <div data-testid="tax-lines">
        {row('Gross written premium', data.gross_premium)}
        {t.levy_lines.map(l => row(`${l.name} — ${l.state} (${l.percent}% share) at ${l.rate_display}`, l.amount))}
        {row(`GST on premium${t.levy_lines.length ? ' and levies' : ''}`, t.gst)}
        {t.duty_lines.map(d => row(`Stamp duty — ${d.state} ${d.percent}% at ${d.rate_display}`, d.duty,
          d.exempt ? { badge: `Exempt — ${d.exempt_reason}` } : {}))}
        {row('Total payable', t.total_payable, { total: true })}
      </div>

      {t.warnings.length > 0 && (
        <div className="mt-2 space-y-0.5" data-testid="tax-warnings">
          {t.warnings.map((w, i) => <p key={i} className="text-[10px]" style={{ color: '#A8720E' }}>⚠ {w}</p>)}
        </div>
      )}

      {data.sbe.eligible_class && !data.sbe.declaration_held && (
        <div className="mt-3 p-3 rounded-sm" style={{ background: 'rgba(232,160,32,0.08)', border: '1px solid rgba(232,160,32,0.3)' }} data-testid="sbe-prompt">
          <p className="text-xs font-semibold" style={{ color: '#A8720E' }}>
            NSW small business exemption available — saves {fmt$(data.sbe.potential_saving)} on this quote
          </p>
          <p className="text-[11px] mt-1" style={{ color: '#5C6B7A' }}>
            Eligible class. If the client's <strong>aggregated</strong> turnover (own + affiliates + connected entities,
            Australia-wide) is under $2M, a signed declaration held <strong>before inception</strong> removes NSW duty
            for {data.sbe.financial_year}.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <a href={`${apiBase}/documents/sbe-declaration.pdf?entity_id=${data.entity_id}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[11px] font-medium"
              style={{ border: '1px solid rgba(168,114,14,0.4)', color: '#A8720E' }} data-testid="sbe-download-btn">
              <FileDown className="w-3 h-3" />Declaration form (PDF)
            </a>
            {isStaff && (
              <>
                <input type="number" placeholder="Aggregated turnover $" value={turnover} onChange={e => setTurnover(e.target.value)}
                  className="px-2 py-1.5 border border-input rounded-sm text-[11px] w-40 bg-white" data-testid="sbe-turnover-input" />
                <input placeholder="Signed by" value={signedBy} onChange={e => setSignedBy(e.target.value)}
                  className="px-2 py-1.5 border border-input rounded-sm text-[11px] w-32 bg-white" data-testid="sbe-signedby-input" />
                <button onClick={record} disabled={saving} data-testid="sbe-record-btn"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-[11px] font-semibold text-white"
                  style={{ background: '#A8720E' }}>
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}Record signed declaration
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {data.sbe.eligible_class && data.sbe.declaration_held && (
        <p className="mt-2 text-[11px] font-medium" style={{ color: '#1D9E75' }} data-testid="sbe-held-note">
          ✓ SBE declaration held for {data.sbe.financial_year} — NSW duty exempt on this quote.
        </p>
      )}
    </Card>
  );
}
